<template>
  <ActionButton
    class="planning-pool-action-button"
    mode="secondary"
    :actions="availableActions"
    :loading="loading"
  />
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import type { PropType, Ref } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { RygenMenuItem } from '@rygen/rygen-ui/model';

import {
  AddShipOrderToShipmentPayload,
  CreateShipmentFromShipOrdersPayload,
  PlanningPoolAction,
  PlanningStatus,
  ProposedShipment,
  RemoveFromPlanningPoolPayload,
  RemoveShipOrderFromShipmentPayload,
  ValidShipOrder,
} from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';

import ActionButton from '@rygen/rygen-ui/components/button/ActionButton.vue';

const props = defineProps({
  selectedShipOrders: {
    type: Array as PropType<ValidShipOrder[]>,
    default: () => [],
  },
  proposedShipments: {
    type: Array as PropType<ProposedShipment[] | RoutePlanProposedShipment[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { selectedShipOrders, proposedShipments, loading } = toRefs(props);
const emit = defineEmits<{
  (event: 'add-to-shipment', payload: AddShipOrderToShipmentPayload): void;
  (
    event: 'create-shipment',
    payload: CreateShipmentFromShipOrdersPayload,
  ): void;
  (
    event: 'remove-from-planning-pool',
    payload: RemoveFromPlanningPoolPayload,
  ): void;
  (
    event: 'remove-from-shipment',
    payload: RemoveShipOrderFromShipmentPayload,
  ): void;
}>();

const isTransportSegmentEnabled = useFlag(
  'route-builder-transport-segment-feat-flag',
);
const { t } = useI18n();

const isSingleShipOrderSelected = computed(
  () => selectedShipOrders.value.length === 1,
);

const allHaveSameStatus = computed(() => {
  return selectedShipOrders.value.every(
    (so, _i, self) => so.planningStatus === self[0]?.planningStatus,
  );
});

const status = computed(() => {
  const firstStatus = selectedShipOrders.value[0]?.planningStatus;
  return !!firstStatus && allHaveSameStatus.value ? firstStatus : undefined;
});

const addToShipmentCommands: Ref<RygenMenuItem[]> = computed(() => {
  if (!isSingleShipOrderSelected.value) {
    return [];
  }

  const shipOrder = selectedShipOrders.value[0];
  return shipOrder.planningStatus !== PlanningStatus.PLANNED
    ? proposedShipments.value.map((s) => ({
        label: t('common.paramNum', {
          param: t('shipments.shipments'),
          num: s.identifier,
        }),
        icon: PrimeIcons.PLUS_CIRCLE,
        command: () =>
          emit('add-to-shipment', {
            shipOrder,
            shipmentIdentifier: s.identifier,
          }),
      }))
    : [];
});

const createEmptyAction = (action: PlanningPoolAction) => ({
  key: action,
  label: '',
  command: () => '',
  visible: false,
});

const actions: Ref<{
  [action in PlanningPoolAction]: RygenMenuItem;
}> = computed(() => ({
  [PlanningPoolAction.CREATE_SHIPMENT]: {
    key: PlanningPoolAction.CREATE_SHIPMENT,
    label: t('common.openParam', {
      param: t('routeBuilder.planningModal'),
    }),
    icon: PrimeIcons.DIRECTIONS,
    command: () =>
      emit('create-shipment', { shipOrders: selectedShipOrders.value }),
    visible: allHaveSameStatus.value && status.value !== PlanningStatus.PLANNED,
  },
  [PlanningPoolAction.ADD_TO_SHIPMENT]: {
    key: PlanningPoolAction.ADD_TO_SHIPMENT,
    label: t('common.addToParam', {
      param: t('shipments.shipments'),
    }),
    icon: PrimeIcons.PLUS_CIRCLE,
    items: addToShipmentCommands.value,
    visible:
      isSingleShipOrderSelected.value && !!addToShipmentCommands.value.length,
  },
  [PlanningPoolAction.REMOVE_FROM_PLANNING_POOL]: {
    key: PlanningPoolAction.REMOVE_FROM_PLANNING_POOL,
    label: t('common.removeFromParam', {
      param: t('routeBuilder.planningPool'),
    }),
    icon: PrimeIcons.MINUS,
    // TODO: ticket says show it disabled if any shiporder is on a shipment, with an explanatory tooltip; requires rygen ui change
    command: () =>
      emit('remove-from-planning-pool', {
        shipOrders: selectedShipOrders.value,
      }),
  },
  [PlanningPoolAction.REMOVE_FROM_SHIPMENT]: createEmptyAction(
    PlanningPoolAction.REMOVE_FROM_SHIPMENT,
  ),
}));

const actionsByStatus: Ref<{
  [status in PlanningStatus]: RygenMenuItem[];
}> = computed(() => {
  const partiallyPlannedActions = isTransportSegmentEnabled.value
    ? [
        actions.value[PlanningPoolAction.CREATE_SHIPMENT],
        actions.value[PlanningPoolAction.ADD_TO_SHIPMENT],
      ]
    : [actions.value[PlanningPoolAction.ADD_TO_SHIPMENT]];

  const plannedActions = isTransportSegmentEnabled.value
    ? [
        actions.value[PlanningPoolAction.CREATE_SHIPMENT],
        actions.value[PlanningPoolAction.ADD_TO_SHIPMENT],
      ]
    : [];

  return {
    [PlanningStatus.UNPLANNED]: [
      actions.value[PlanningPoolAction.CREATE_SHIPMENT],
      actions.value[PlanningPoolAction.ADD_TO_SHIPMENT],
      actions.value[PlanningPoolAction.REMOVE_FROM_PLANNING_POOL],
    ],
    [PlanningStatus.PARTIALLY_PLANNED]: partiallyPlannedActions,
    [PlanningStatus.PLANNED]: plannedActions,
  };
});

const availableActions = computed(() => {
  if (allHaveSameStatus.value && status.value) {
    return actionsByStatus.value[status.value];
  }

  const statuses = selectedShipOrders.value.map((s) => s.planningStatus);
  const allAvailableActions = statuses
    .map((status) => actionsByStatus.value[status])
    .flat()
    .filter((action) => {
      // Filter out actions that aren't available in each of the selected statuses
      return statuses
        .map((status) => actionsByStatus.value[status])
        .every((actions) => actions.includes(action));
    });
  return Array.from(new Set(allAvailableActions));
});
</script>
